import { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWallet, useCashbackClaims, useRedemptions, usePayouts, useStripeConnect } from '../../hooks/useWallet';
import { useTier, useReferrals } from '../../hooks/useUserExtras';
import { LoadingPage, EmptyState, Spinner, useConfirm } from '../../components/common/UI';
import { useToast } from '../../components/common/Toast';
import { formatDiscount } from '../../utils/helpers';
import api from '../../services/api';

const TIER_META = {
  silver:   { name: 'Silver',   color: '#94A3B8', icon: '🥈', next: 'Gold',     to: 50 },
  gold:     { name: 'Gold',     color: '#F59E0B', icon: '🥇', next: 'Platinum', to: 250 },
  platinum: { name: 'Platinum', color: '#A855F7', icon: '💎', next: null,       to: null },
};

const STATUS_META = {
  pending:    { label: 'Pending',    color: '#F59E0B', bg: '#FEF3C7' },
  approved:   { label: 'Available',  color: '#0F9D6E', bg: '#E6F7F0' },
  paid:       { label: 'Paid',       color: '#6B2FD9', bg: '#F3EEFF' },
  rejected:   { label: 'Rejected',   color: '#DC2626', bg: '#FDECEA' },
  processing: { label: 'Processing', color: '#F59E0B', bg: '#FEF3C7' },
  failed:     { label: 'Failed',     color: '#DC2626', bg: '#FDECEA' },
  reversed:   { label: 'Reversed',   color: '#DC2626', bg: '#FDECEA' },
};

/* ================== Layout shell ================== */

export function ProfileLayout() {
  const { user } = useAuth();

  if (!user) return <LoadingPage />;

  const tabs = [
    { to: '/profile',             label: 'Account',        icon: '👤', end: true },
    { to: '/profile/wallet',      label: 'Cashback Wallet',icon: '💰' },
    { to: '/profile/redemptions', label: 'Redemptions',    icon: '🎟️' },
    { to: '/profile/payouts',     label: 'Payouts',        icon: '💸' },
    { to: '/profile/tier',        label: 'Loyalty Tier',   icon: '🏆' },
    { to: '/profile/referrals',   label: 'Refer & Earn',   icon: '🎁' },
    { to: '/profile/settings',    label: 'Settings',       icon: '⚙️' },
  ];

  return (
    <div style={{ background: '#F7F8FC', minHeight: 'calc(100vh - 72px)' }}>
      {/* Header banner */}
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '40px 0 80px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-40%', right: '-10%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,70,239,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 28, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', flexShrink: 0, border: '3px solid rgba(255,255,255,0.2)' }}>
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, opacity: 0.8, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Welcome back</p>
            <h1 style={{ fontSize: 'clamp(24px, 3.4vw, 36px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, marginTop: 4 }}>{user.name}</h1>
            <p style={{ fontSize: 14, opacity: 0.85, marginTop: 4 }}>{user.email}</p>
          </div>
        </div>
      </section>

      {/* Tabs row */}
      <div className="container" style={{ marginTop: -32, position: 'relative', paddingBottom: 8 }}>
        <div style={{ background: '#fff', border: '1px solid #E4E7F0', borderRadius: 14, padding: 6, display: 'flex', gap: 4, overflowX: 'auto', boxShadow: '0 4px 18px rgba(15,18,37,0.06)' }}>
          {tabs.map(t => (
            <NavLink key={t.to} to={t.to} end={t.end}
              style={({ isActive }) => ({
                padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                color: isActive ? '#fff' : '#4A5568',
                background: isActive ? 'var(--brand-gradient)' : 'transparent',
                textDecoration: 'none', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
                transition: 'all 0.15s',
                boxShadow: isActive ? 'var(--shadow-glow)' : 'none',
              })}
            >
              <span>{t.icon}</span> {t.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="container" style={{ padding: '24px 20px 64px' }}>
        <Outlet />
      </div>
    </div>
  );
}

/* ================== Wallet page ================== */

export function WalletPage() {
  const { wallet, loading, error, refetch } = useWallet();
  const { claims, loading: claimsLoading } = useCashbackClaims();
  const { startOnboarding, refresh, loading: connecting } = useStripeConnect();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  // Handle return-from-Stripe. Build a fresh URLSearchParams so React detects
  // the change (mutating in place doesn't trigger setSearchParams' equality check).
  useEffect(() => {
    if (searchParams.get('stripe') === 'connected') {
      refresh().then(() => {
        refetch();
        const next = new URLSearchParams(searchParams);
        next.delete('stripe');
        setSearchParams(next, { replace: true });
      });
    }
  }, []); // eslint-disable-line

  if (loading) return <LoadingPage />;

  if (error || !wallet) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div className="alert alert-danger">{error || 'Could not load your wallet.'}</div>
        <button className="btn btn-secondary" onClick={refetch} style={{ marginTop: 16 }}>Try again</button>
      </div>
    );
  }

  const b = wallet.balances || {};
  const tier = TIER_META[wallet.tier] || TIER_META.silver;
  const lifetime = Number(b.lifetime_earned) || 0;
  const tierProgress = tier.to ? Math.min(100, (lifetime / tier.to) * 100) : 100;
  const stripe = wallet.stripe_account || { connected: false };
  const minPayout = Number(b.minimum_payout) || 0;
  const available = Number(b.available) || 0;
  const canRequestPayout = stripe.payouts_enabled && available >= minPayout;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 24 }} className="wallet-grid">

      {/* Left column */}
      <div>
        {/* Available balance card */}
        <div style={{ background: 'var(--brand-gradient)', borderRadius: 20, padding: 32, color: '#fff', position: 'relative', overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ position: 'absolute', top: -50, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
          <div style={{ position: 'absolute', bottom: -60, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'relative' }}>
            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85 }}>Available cashback</p>
            <p style={{ fontSize: 'clamp(40px, 6vw, 64px)', fontWeight: 800, letterSpacing: '-0.03em', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', lineHeight: 1, margin: '12px 0 4px' }}>${b.available?.toFixed(2) ?? '0.00'}</p>
            <p style={{ fontSize: 13, opacity: 0.85 }}>Min. payout ${b.minimum_payout?.toFixed(2)} · {b.currency}</p>

            <button
              onClick={() => setShowPayoutModal(true)}
              disabled={!canRequestPayout}
              className="btn"
              style={{
                marginTop: 24, padding: '14px 28px', borderRadius: 999,
                background: canRequestPayout ? '#fff' : 'rgba(255,255,255,0.18)',
                color: canRequestPayout ? '#1A1B4B' : 'rgba(255,255,255,0.6)',
                fontWeight: 700, fontSize: 15, border: 'none',
                cursor: canRequestPayout ? 'pointer' : 'not-allowed',
              }}
            >
              {canRequestPayout ? `💸 Withdraw $${b.available?.toFixed(2)}` :
               !stripe.payouts_enabled ? '🔌 Connect bank to withdraw' :
               `Min. $${b.minimum_payout?.toFixed(2)} to withdraw`}
            </button>
          </div>
        </div>

        {/* Other balances */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
          <BalanceTile label="Pending" value={b.pending} hint={`Locked ${b.lock_period_days}d`} />
          <BalanceTile label="In Payout" value={b.in_payout} hint="Processing" />
          <BalanceTile label="Lifetime Paid" value={b.paid_lifetime} hint="All time" />
          <BalanceTile label="Lifetime Earned" value={b.lifetime_earned} hint="All time" gradient />
        </div>

        {/* Claims history */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', overflow: 'hidden' }}>
          <div style={{ padding: '18px 22px', borderBottom: '1px solid #F0F2F8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontWeight: 700, fontSize: 15 }}>Cashback History</p>
            <Link to="/profile/redemptions" style={{ fontSize: 13, color: '#6B2FD9', fontWeight: 600 }}>View all redemptions →</Link>
          </div>
          {claimsLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><Spinner size={28} /></div>
          ) : claims.length === 0 ? (
            <EmptyState icon="🎁" title="No cashback yet" subtitle="Redeem a deal with cashback enabled and watch this list fill up." />
          ) : (
            <div>
              {claims.slice(0, 10).map(c => <ClaimRow key={c.id} claim={c} />)}
            </div>
          )}
        </div>
      </div>

      {/* Right column */}
      <div>
        {/* Tier card */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', padding: 24, marginBottom: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#8B94A8', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8 }}>Your Tier</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 36 }}>{tier.icon}</span>
            <div>
              <p style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: tier.color }}>{tier.name}</p>
              {tier.next && <p style={{ fontSize: 12, color: '#8B94A8' }}>${(tier.to - (b.lifetime_earned ?? 0)).toFixed(2)} to {tier.next}</p>}
              {!tier.next && <p style={{ fontSize: 12, color: '#0F9D6E', fontWeight: 600 }}>✨ Top tier reached</p>}
            </div>
          </div>
          <div style={{ height: 6, background: '#F0F2F8', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${tierProgress}%`, background: 'var(--brand-gradient)', transition: 'width 0.3s' }} />
          </div>
          <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 14, lineHeight: 1.5 }}>
            Higher tiers unlock bonus cashback, early access, and exclusive deals.
          </p>
        </div>

        {/* Points card */}
        <div style={{ background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 16, padding: 24, marginBottom: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#6B2FD9', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8 }}>PerkVora Points</p>
          <p className="gradient-text" style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.025em', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', lineHeight: 1 }}>{(wallet?.lifetime_points ?? 0).toLocaleString()}</p>
          <p style={{ fontSize: 13, color: '#4A5568', marginTop: 8 }}>Earned across all redemptions</p>
        </div>

        {/* Stripe Connect card */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', padding: 24 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#8B94A8', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 12 }}>Payout Method</p>

          {!stripe.connected ? (
            <>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: '#635BFF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800 }}>S</div>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 14 }}>Connect with Stripe</p>
                  <p style={{ fontSize: 12, color: '#8B94A8' }}>Get paid via ACH or debit card</p>
                </div>
              </div>
              <button onClick={startOnboarding} disabled={connecting} className="btn btn-primary btn-full">
                {connecting ? 'Connecting…' : 'Connect bank account →'}
              </button>
              <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 12, lineHeight: 1.5, textAlign: 'center' }}>
                Powered by Stripe. We never see your bank details.
              </p>
            </>
          ) : !stripe.onboarding_complete ? (
            <>
              <p style={{ fontSize: 13, color: '#4A5568', marginBottom: 16, lineHeight: 1.55 }}>
                ⏳ Stripe onboarding incomplete. Finish setup to receive payouts.
              </p>
              <button onClick={startOnboarding} className="btn btn-primary btn-full">Resume onboarding →</button>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', background: '#E6F7F0', borderRadius: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>✓</span>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 13, color: '#0F9D6E' }}>Stripe connected</p>
                  <p style={{ fontSize: 11, color: '#0F9D6E' }}>{wallet?.payout_method?.display_name || 'Ready for payouts'}</p>
                </div>
              </div>
              <p style={{ fontSize: 12, color: '#8B94A8' }}>Payouts arrive in 1–3 business days.</p>
            </>
          )}
        </div>
      </div>

      <style>{`@media(max-width:880px){.wallet-grid{grid-template-columns:1fr!important}}`}</style>

      {showPayoutModal && (
        <RequestPayoutModal
          available={b.available}
          minimum={b.minimum_payout}
          onClose={() => setShowPayoutModal(false)}
          onSuccess={() => { setShowPayoutModal(false); refetch(); }}
        />
      )}
    </div>
  );
}

/* ================== Redemptions page ================== */

export function RedemptionsPage() {
  const { redemptions, loading } = useRedemptions();

  if (loading) return <LoadingPage />;

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', overflow: 'hidden' }}>
      <div style={{ padding: '18px 22px', borderBottom: '1px solid #F0F2F8' }}>
        <p style={{ fontWeight: 700, fontSize: 15 }}>Your Redemptions</p>
        <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 2 }}>Every deal you've claimed and the cashback status for each.</p>
      </div>
      {redemptions.length === 0 ? (
        <EmptyState icon="🎟️" title="No redemptions yet" subtitle="Browse the deal feed and grab your first one." action={<Link to="/coupons" className="btn btn-primary">Browse deals →</Link>} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="ds-table">
            <thead><tr><th>Deal</th><th>Date</th><th style={{textAlign:'right'}}>Order Value</th><th style={{textAlign:'right'}}>Cashback</th><th>Status</th></tr></thead>
            <tbody>
              {redemptions.map(r => {
                const claim = r.cashback_claim;
                const status = claim ? STATUS_META[claim.status] : null;
                return (
                  <tr key={r.id}>
                    <td>
                      <p style={{ fontWeight: 600, color: '#0F1225' }}>{r.coupon?.title || 'Coupon'}</p>
                      <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 2 }}>{r.coupon?.merchant?.name} · <span className="badge badge-gradient" style={{ fontSize: 10 }}>{formatDiscount(r.coupon)}</span></p>
                    </td>
                    <td style={{ fontSize: 13, color: '#4A5568' }}>{r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : '—'}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>${Number(r.order_value || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{claim ? `$${Number(claim.amount).toFixed(2)}` : <span style={{ color: '#8B94A8', fontWeight: 400 }}>—</span>}</td>
                    <td>{status ? <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, color: status.color, background: status.bg }}>{status.label}</span> : <span style={{ color: '#8B94A8', fontSize: 13 }}>No cashback</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================== Payouts page ================== */

export function PayoutsPage() {
  const { payouts, loading } = usePayouts();
  if (loading) return <LoadingPage />;

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', overflow: 'hidden' }}>
      <div style={{ padding: '18px 22px', borderBottom: '1px solid #F0F2F8' }}>
        <p style={{ fontWeight: 700, fontSize: 15 }}>Payout History</p>
      </div>
      {payouts.length === 0 ? (
        <EmptyState icon="💸" title="No payouts yet" subtitle="Once you withdraw cashback, your payout history will appear here." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="ds-table">
            <thead><tr><th>Date</th><th>Method</th><th style={{textAlign:'right'}}>Amount</th><th>Status</th><th>Reference</th></tr></thead>
            <tbody>
              {payouts.map(p => {
                const status = STATUS_META[p.status];
                return (
                  <tr key={p.id}>
                    <td style={{ fontSize: 13 }}>{new Date(p.requested_at || p.created_at).toLocaleDateString()}</td>
                    <td style={{ fontSize: 13 }}>{p.payout_method?.display_name || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>${Number(p.amount).toFixed(2)}</td>
                    <td>{status && <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, color: status.color, background: status.bg }}>{status.label}</span>}</td>
                    <td style={{ fontSize: 11, color: '#8B94A8', fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{p.stripe_transfer_id || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================== Tier page ================== */

const TIER_ICONS = { silver: '🥈', gold: '🥇', platinum: '💎', bronze: '🥉', diamond: '💠' };
const TIER_COLORS = { silver: '#94A3B8', gold: '#F59E0B', platinum: '#A855F7', bronze: '#CD7F32', diamond: '#22D3EE' };

export function TierPage() {
  const { tier, loading, error } = useTier();

  if (loading) return <LoadingPage />;
  if (error || !tier) {
    return <div className="alert alert-danger">{error || 'Could not load tier info.'}</div>;
  }

  const current = tier.current || {};
  const next    = tier.next;
  const progress = tier.progress || {};
  const all      = tier.all || [];
  const accent   = TIER_COLORS[current.key] || '#6B2FD9';

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {/* Hero card */}
      <div style={{ background: '#fff', borderRadius: 20, border: '1px solid #E4E7F0', overflow: 'hidden' }}>
        <div style={{ background: `linear-gradient(135deg, ${accent}, #6B2FD9)`, color: '#fff', padding: '32px 32px 80px', position: 'relative' }}>
          <div style={{ position: 'absolute', top: -50, right: -40, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.10)' }} />
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85, position: 'relative' }}>Your tier</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 12, position: 'relative' }}>
            <span style={{ fontSize: 64, lineHeight: 1 }}>{TIER_ICONS[current.key] || '🏆'}</span>
            <div>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1 }}>{current.name || current.key}</h2>
              {current.tagline && <p style={{ fontSize: 14, opacity: 0.85, marginTop: 4 }}>{current.tagline}</p>}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ padding: '24px 32px', marginTop: -56, position: 'relative' }}>
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E4E7F0', padding: 22, boxShadow: '0 4px 18px rgba(15,18,37,0.08)' }}>
            {next ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <p style={{ fontSize: 13, color: '#4A5568' }}>
                    <strong>${Number(progress.amount_to_next || 0).toFixed(2)}</strong> earned cashback to unlock <strong style={{ color: accent }}>{next.name || next.key}</strong>
                  </p>
                  <p style={{ fontSize: 12, color: '#8B94A8', fontVariantNumeric: 'tabular-nums' }}>
                    ${Number(progress.current_earned || 0).toFixed(2)} / ${Number(progress.next_threshold || 0).toFixed(2)}
                  </p>
                </div>
                <div style={{ height: 10, borderRadius: 999, background: '#F0F2F8', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${progress.percent_to_next || 0}%`, background: `linear-gradient(90deg, ${accent}, #6B2FD9)`, transition: 'width 0.4s ease' }} />
                </div>
              </>
            ) : (
              <p style={{ fontSize: 14, fontWeight: 600, color: '#0F9D6E', textAlign: 'center' }}>🎉 Top tier unlocked. Keep earning to maintain it.</p>
            )}
            <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 12 }}>
              Based on your last {Math.round((tier.qualifying_period_days || 365) / 30)} months of approved cashback.
            </p>
          </div>
        </div>
      </div>

      {/* Perks unlocked. Each perk is { icon, title, desc } per
          config/loyalty.php — rendering must address each field; rendering
          the raw object here threw React error #31 ("objects are not valid
          as a React child"). */}
      {current.perks && current.perks.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', padding: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Perks unlocked at {current.name}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {current.perks.map((p, i) => {
              // Be tolerant of both shapes: legacy string perks and the new
              // {icon, title, desc} objects, in case anyone rolls back the
              // backend without redeploying the frontend.
              if (typeof p === 'string') {
                return (
                  <div key={i} style={{ padding: 16, background: '#F7F8FC', borderRadius: 12, fontSize: 13, color: '#4A5568', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ color: accent, fontSize: 16, lineHeight: 1, flexShrink: 0 }}>✓</span>
                    <span>{p}</span>
                  </div>
                );
              }
              return (
                <div key={i} style={{ padding: 16, background: '#F7F8FC', borderRadius: 12, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{p.icon || '✓'}</span>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#0F1225' }}>{p.title}</p>
                    {p.desc && <p style={{ fontSize: 12, color: '#5A6378', marginTop: 4, lineHeight: 1.5 }}>{p.desc}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tier ladder */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #F0F2F8' }}>
          <p style={{ fontWeight: 700, fontSize: 15 }}>The Ladder</p>
        </div>
        <div>
          {all.map(t => {
            const reached = (progress.current_earned ?? 0) >= (t.min_earned ?? 0);
            const isCurrent = t.key === current.key;
            const tColor = TIER_COLORS[t.key] || '#94A3B8';
            return (
              <div key={t.key} style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '16px 22px', borderBottom: '1px solid #F7F8FC',
                background: isCurrent ? 'rgba(107,47,217,0.04)' : 'transparent',
              }}>
                <span style={{ fontSize: 28, opacity: reached ? 1 : 0.3 }}>{TIER_ICONS[t.key] || '🏆'}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: reached ? '#0F1225' : '#8B94A8' }}>
                    {t.name || t.key}
                    {isCurrent && <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 999, background: tColor, color: '#fff', fontWeight: 600 }}>YOU</span>}
                  </p>
                  <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 2 }}>
                    ${Number(t.min_earned).toFixed(0)}+ earned in {Math.round((tier.qualifying_period_days || 365) / 30)}mo
                  </p>
                </div>
                {reached
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: '#0F9D6E' }}>UNLOCKED</span>
                  : <span style={{ fontSize: 11, fontWeight: 700, color: '#8B94A8' }}>LOCKED</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ================== Referrals page ================== */

export function ReferralsPage() {
  const { stats, loading, error, refetch, regenerate } = useReferrals();
  const [confirm, confirmNode] = useConfirm();
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  if (loading) return <LoadingPage />;
  if (error || !stats) return <div className="alert alert-danger">{error || 'Could not load referrals.'}</div>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(stats.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join PerkVora and earn $' + stats.referee_bonus.toFixed(0),
          text: `I'm using PerkVora for cashback + verified coupons. Sign up with my link and we both get $${stats.referee_bonus.toFixed(0)}.`,
          url: stats.share_url,
        });
      } catch { /* user cancelled */ }
    } else {
      handleCopy();
    }
  };

  const handleRegenerate = async () => {
    if (!(await confirm({
      title: 'Generate a new code?',
      message: "Old links you've already shared will stop working. People you've already referred stay attributed.",
      confirmLabel: 'Generate new code',
    }))) return;
    setRegenerating(true);
    try { await regenerate(); await refetch(); }
    finally { setRegenerating(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {/* Hero */}
      <div style={{ background: 'var(--brand-gradient)', borderRadius: 20, padding: 32, color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -40, width: 240, height: 240, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
        <div style={{ position: 'relative' }}>
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85 }}>Refer & earn</p>
          <h2 style={{ fontSize: 'clamp(26px, 3.6vw, 36px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, marginTop: 8 }}>
            Give ${stats.referee_bonus.toFixed(0)}, get ${stats.referrer_bonus.toFixed(0)}
          </h2>
          <p style={{ fontSize: 14, opacity: 0.9, marginTop: 8, maxWidth: 540 }}>
            Friends who sign up with your link get ${stats.referee_bonus.toFixed(0)} on their first redemption.
            You get ${stats.referrer_bonus.toFixed(0)} once they qualify.
          </p>

          {/* Share row */}
          <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 280px', background: 'rgba(255,255,255,0.16)', borderRadius: 12, padding: '12px 16px', backdropFilter: 'blur(8px)' }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.8 }}>Your link</p>
              <p style={{ fontSize: 13, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>{stats.share_url}</p>
            </div>
            <button onClick={handleCopy}
              style={{ padding: '12px 20px', borderRadius: 12, background: '#fff', color: '#1A1B4B', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}>
              {copied ? '✓ Copied' : '📋 Copy link'}
            </button>
            <button onClick={handleShare}
              style={{ padding: '12px 20px', borderRadius: 12, background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700, fontSize: 14, border: '1.5px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>
              📤 Share
            </button>
          </div>

          <p style={{ fontSize: 12, opacity: 0.85, marginTop: 12 }}>
            Or share your code: <strong style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{stats.code}</strong>
            {' · '}
            <button onClick={handleRegenerate} disabled={regenerating}
              style={{ background: 'none', border: 'none', color: '#fff', textDecoration: 'underline', fontSize: 12, cursor: 'pointer', padding: 0, opacity: regenerating ? 0.5 : 1 }}>
              {regenerating ? 'regenerating…' : 'regenerate'}
            </button>
          </p>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <ReferralStat label="Invited"   value={stats.invited}   />
        <ReferralStat label="Pending"   value={stats.pending}   hint="Signed up, not redeemed yet" />
        <ReferralStat label="Qualified" value={stats.qualified} hint="Made first redemption" />
        <ReferralStat label="Earned"    value={`$${Number(stats.lifetime_earned).toFixed(2)}`} highlight />
      </div>

      {/* History */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #F0F2F8' }}>
          <p style={{ fontWeight: 700, fontSize: 15 }}>Recent referrals</p>
        </div>
        {(!stats.history || stats.history.length === 0) ? (
          <EmptyState icon="🎁" title="No referrals yet" subtitle="Share your link above to invite friends. You'll see them appear here as soon as they sign up." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ds-table">
              <thead><tr><th>Friend</th><th>Status</th><th style={{textAlign:'right'}}>Bonus</th><th>Signed up</th></tr></thead>
              <tbody>
                {stats.history.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontSize: 13, color: '#0F1225', fontWeight: 600 }}>{r.referee_name || 'Friend'}</td>
                    <td>
                      <span className={`badge ${r.status === 'qualified' ? 'badge-green' : r.status === 'pending' ? 'badge-yellow' : 'badge-red'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: r.status === 'qualified' ? 700 : 400, color: r.status === 'qualified' ? '#0F9D6E' : '#8B94A8' }}>
                      {r.status === 'qualified' ? `$${Number(r.bonus_amount).toFixed(2)}` : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#8B94A8' }}>{r.signed_up_at ? new Date(r.signed_up_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* How-it-works helper */}
      <div style={{ background: '#F7F8FC', borderRadius: 12, padding: 22 }}>
        <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>How it works</p>
        <ol style={{ fontSize: 13, color: '#4A5568', lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
          <li>Share your link — <strong>they get ${Number(stats.referee_bonus).toFixed(0)} bonus</strong> on signup.</li>
          <li>They redeem any coupon and the bonus moves from "pending" to "qualified".</li>
          <li><strong>You get ${Number(stats.referrer_bonus).toFixed(0)}</strong> credited to your wallet at the same moment.</li>
        </ol>
      </div>
      {confirmNode}
    </div>
  );
}

function ReferralStat({ label, value, hint, highlight }) {
  return (
    <div style={{
      background: highlight ? 'var(--brand-gradient)' : '#fff',
      color: highlight ? '#fff' : '#0F1225',
      borderRadius: 14,
      border: highlight ? 'none' : '1px solid #E4E7F0',
      padding: 20,
    }}>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: highlight ? 0.85 : 1, color: highlight ? '#fff' : '#8B94A8' }}>{label}</p>
      <p style={{ fontSize: 28, fontWeight: 800, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', letterSpacing: '-0.025em', marginTop: 4 }}>{value}</p>
      {hint && <p style={{ fontSize: 11, color: highlight ? 'rgba(255,255,255,0.85)' : '#8B94A8', marginTop: 2 }}>{hint}</p>}
    </div>
  );
}

/* ================== Account page ================== */

export function AccountPage() {
  const { user, refreshUser } = useAuth();
  const { wallet } = useWallet();
  const tier = TIER_META[wallet?.tier] || TIER_META.silver;
  const [editing, setEditing] = useState(false);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }} className="account-grid">
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', padding: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 18 }}>Account Details</h2>
        <Field label="Name" value={user?.name} />
        <Field label="Email" value={user?.email} />
        <Field label="Phone" value={user?.phone || 'Not set'} />
        <Field label="Location" value={[user?.city, user?.state, user?.country].filter(Boolean).join(', ') || 'Not set'} />
        <Field label="Member since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'} />
        <button className="btn btn-outline" style={{ marginTop: 14 }} onClick={() => setEditing(true)}>Edit profile</button>
        {editing && <EditProfileModal user={user} onClose={() => setEditing(false)} onSaved={refreshUser} />}
      </div>

      <div>
        <div style={{ background: 'var(--brand-gradient)', borderRadius: 16, padding: 24, color: '#fff', marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -40, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />
          <div style={{ position: 'relative' }}>
            <p style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Available Cashback</p>
            <p style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.025em', marginTop: 8, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>${wallet?.balances?.available?.toFixed(2) ?? '0.00'}</p>
            <Link to="/profile/wallet" className="btn" style={{ marginTop: 14, background: '#fff', color: '#1A1B4B', borderRadius: 999, padding: '10px 18px', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>Open wallet →</Link>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', padding: 20 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#8B94A8', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 10 }}>Tier</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 28 }}>{tier.icon}</span>
            <p style={{ fontSize: 18, fontWeight: 800, color: tier.color }}>{tier.name}</p>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:880px){.account-grid{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
}

/* ================== Edit Profile modal ================== */

function EditProfileModal({ user, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name:     user?.name     || '',
    phone:    user?.phone    || '',
    city:     user?.city     || '',
    state:    user?.state    || '',
    zip_code: user?.zip_code || '',
    country:  user?.country  || 'US',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const f = (k) => ({ value: form[k], onChange: (e) => setForm(p => ({ ...p, [k]: e.target.value })) });
  const fieldError = (k) => errors[k] && <p style={{ color: '#DC2626', fontSize: 12, marginTop: 4 }}>{errors[k]}</p>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      // Strip blanks so backend's nullable rules pass without forcing the
      // user to keep stale Location strings.
      const payload = Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== ''));
      await api.put('/auth/me', payload);
      toast.success('Profile updated');
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      const fields = err.response?.data?.errors;
      if (fields) {
        const flat = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]));
        setErrors(flat);
        // Only name and phone used to render their errors, so a bad ZIP or
        // city failed with no message at all (QA 09-18 #4). Every field shows
        // its own now; anything else still reaches the user.
        const unshown = Object.keys(flat).filter(k => !['name', 'phone', 'city', 'state', 'zip_code', 'country'].includes(k));
        if (unshown.length) toast.error(flat[unshown[0]]);
      }
      else toast.error(err.response?.data?.message || 'Could not save profile.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit profile</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ fontSize: 13, color: '#4A5568', marginBottom: 18 }}>
              Email and password aren't here — they each have their own flow under Settings.
            </p>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" required autoComplete="name" {...f('name')} />
              {errors.name && <p style={{ color: '#DC2626', fontSize: 12, marginTop: 4 }}>{errors.name}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" type="tel" autoComplete="tel" {...f('phone')} placeholder="+1 555 123 4567" />
              {errors.phone && <p style={{ color: '#DC2626', fontSize: 12, marginTop: 4 }}>{errors.phone}</p>}
            </div>
            {/* Limits match the server's validation (users table widths). The
                autoComplete hints matter: without them Chrome filled ZIP with the
                member's name, which then failed validation (QA 2026-09-21 #3). */}
            <div className="pv-loc-grid">
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-input" autoComplete="address-level2" {...f('city')} maxLength={100} />
                {fieldError('city')}
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input className="form-input" autoComplete="address-level1" {...f('state')} placeholder="CA" maxLength={50} />
                {fieldError('state')}
              </div>
              <div className="form-group">
                <label className="form-label">ZIP</label>
                <input className="form-input" autoComplete="postal-code" {...f('zip_code')} maxLength={10}
                  inputMode={(form.country || 'US').toUpperCase() === 'US' ? 'numeric' : 'text'}
                  placeholder={(form.country || 'US').toUpperCase() === 'US' ? '94105' : ''} />
                {fieldError('zip_code')}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <input className="form-input" autoComplete="country" {...f('country')} maxLength={2} placeholder="US" style={{ textTransform: 'uppercase' }} />
              {fieldError('country')}
            </div>
          </div>
          <div className="modal-footer pv-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ================== Settings page (placeholder) ================== */

export function SettingsPage() {
  const { user, refreshUser, logout } = useAuth();
  const { toast } = useToast();

  // Communication preferences — saved together with one PUT.
  const [prefs, setPrefs] = useState({
    email_opt_in: !!user?.email_opt_in,
    sms_opt_in:   !!user?.sms_opt_in,
    ccpa_opt_out: !!user?.ccpa_opt_out,
  });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    setPrefs({
      email_opt_in: !!user?.email_opt_in,
      sms_opt_in:   !!user?.sms_opt_in,
      ccpa_opt_out: !!user?.ccpa_opt_out,
    });
  }, [user?.email_opt_in, user?.sms_opt_in, user?.ccpa_opt_out]);

  const togglePref = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.put('/auth/me', prefs);
      await refreshUser();
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save preferences.');
    } finally { setSavingPrefs(false); }
  };

  // Password change form.
  const [pw, setPw] = useState({ current_password: '', password: '', password_confirmation: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError]   = useState('');

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pw.password !== pw.password_confirmation) { setPwError('Passwords don\'t match.'); return; }
    if (pw.password.length < 8)                   { setPwError('Password must be at least 8 characters.'); return; }
    setPwSaving(true);
    try {
      const { data } = await api.post('/auth/change-password', pw);
      toast.success(data.message || 'Password updated');
      setPw({ current_password: '', password: '', password_confirmation: '' });
    } catch (err) {
      setPwError(err.response?.data?.error || err.response?.data?.message || 'Could not change password.');
    } finally { setPwSaving(false); }
  };

  // Delete-account flow gated behind confirm modal + current password.
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SettingsCard title="Communication preferences" subtitle="Choose how PerkVora reaches you.">
        <PrefToggle
          label="Marketing email"
          desc="Hand-picked deals, flash drops, weekly digest. Transactional emails (cashback, payouts, password reset) always send regardless."
          checked={prefs.email_opt_in}
          onChange={() => togglePref('email_opt_in')}
        />
        <PrefToggle
          label="SMS"
          desc="Time-sensitive alerts only — expiring favorites, big cashback drops. Requires a verified phone number on your profile."
          checked={prefs.sms_opt_in}
          onChange={() => togglePref('sms_opt_in')}
        />
        <PrefToggle
          label="Opt out of personalization (CCPA)"
          desc="If on, PerkVora won't use your browsing history to personalize the deal feed. You may see fewer relevant deals."
          checked={prefs.ccpa_opt_out}
          onChange={() => togglePref('ccpa_opt_out')}
        />
        <div style={{ marginTop: 14, textAlign: 'right' }}>
          <button className="btn btn-primary" onClick={savePrefs} disabled={savingPrefs}>
            {savingPrefs ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      </SettingsCard>

      <SettingsCard title="Change password" subtitle="Set a new password. Other devices will be signed out for security.">
        <form onSubmit={changePassword}>
          {pwError && <div className="alert alert-danger">{pwError}</div>}
          <div className="form-group">
            <label className="form-label">Current password</label>
            <input className="form-input" type="password" required value={pw.current_password}
              onChange={(e) => setPw(p => ({ ...p, current_password: e.target.value }))} autoComplete="current-password" />
          </div>
          <div className="pv-pair-grid">
            <div className="form-group">
              <label className="form-label">New password</label>
              <input className="form-input" type="password" required minLength={8} value={pw.password}
                onChange={(e) => setPw(p => ({ ...p, password: e.target.value }))} autoComplete="new-password" />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm new password</label>
              <input className="form-input" type="password" required minLength={8} value={pw.password_confirmation}
                onChange={(e) => setPw(p => ({ ...p, password_confirmation: e.target.value }))} autoComplete="new-password" />
            </div>
          </div>
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button className="btn btn-primary" type="submit" disabled={pwSaving}>
              {pwSaving ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </SettingsCard>

      <SettingsCard title="Delete account" subtitle="This removes your account from PerkVora. Available cashback is forfeited.">
        <p style={{ fontSize: 13, color: '#4A5568', lineHeight: 1.6, marginBottom: 14 }}>
          Account deletion is permanent. Pending cashback claims are cancelled,
          your data is removed from personalization, and you'll be signed out
          on every device. If you'd rather just pause marketing emails, untoggle
          the preference above instead.
        </p>
        <button className="btn btn-outline" style={{ color: '#DC2626', borderColor: '#FCA5A5' }} onClick={() => setConfirmingDelete(true)}>
          Delete my account
        </button>
        {confirmingDelete && (
          <DeleteAccountModal
            onClose={() => setConfirmingDelete(false)}
            onDeleted={async () => { await logout(); window.location.assign('/'); }}
          />
        )}
      </SettingsCard>
    </div>
  );
}

function SettingsCard({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E4E7F0', padding: 24 }}>
      <h2 style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 4 }}>{title}</h2>
      {subtitle && <p style={{ fontSize: 13, color: '#4A5568', marginBottom: 18 }}>{subtitle}</p>}
      {children}
    </div>
  );
}

function PrefToggle({ label, desc, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '12px 0', borderTop: '1px solid #F0F2F8' }}>
      <button
        type="button"
        onClick={onChange}
        role="switch"
        aria-checked={checked}
        aria-label={label}
        style={{
          width: 40, height: 22, borderRadius: 999,
          background: checked ? 'var(--brand-gradient)' : '#E4E7F0',
          border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0,
          marginTop: 2, transition: 'background 0.18s',
        }}
      >
        <span style={{
          position: 'absolute', top: 2, left: checked ? 20 : 2,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.18s', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        }} />
      </button>
      <div>
        <p style={{ fontSize: 14, fontWeight: 700, color: '#0F1225' }}>{label}</p>
        <p style={{ fontSize: 12, color: '#4A5568', marginTop: 3, lineHeight: 1.5 }}>{desc}</p>
      </div>
    </div>
  );
}

function DeleteAccountModal({ onClose, onDeleted }) {
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [busy, setBusy]         = useState(false);

  const handleDelete = async (e) => {
    e.preventDefault();
    if (confirm !== 'DELETE') return;
    setBusy(true);
    try {
      await api.delete('/auth/me', { data: { current_password: password } });
      toast.info('Account deleted.');
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete account.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title" style={{ color: '#DC2626' }}>Delete account</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleDelete}>
          <div className="modal-body">
            <p style={{ fontSize: 13, color: '#4A5568', lineHeight: 1.6, marginBottom: 16 }}>
              This will sign you out on every device and remove your account.
              Pending cashback claims are cancelled and cannot be recovered.
            </p>
            <div className="form-group">
              <label className="form-label">Current password</label>
              <input className="form-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <div className="form-group">
              <label className="form-label">Type <strong>DELETE</strong> to confirm</label>
              <input className="form-input" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
          </div>
          <div className="modal-footer pv-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ background: '#DC2626' }} disabled={busy || confirm !== 'DELETE'}>
              {busy ? 'Deleting…' : 'Permanently delete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ================== Sub-components ================== */

function BalanceTile({ label, value, hint, gradient }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E4E7F0', borderRadius: 12, padding: 16 }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: '#8B94A8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>{label}</p>
      <p className={gradient ? 'gradient-text' : ''} style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', color: gradient ? undefined : '#0F1225' }}>
        ${(value ?? 0).toFixed(2)}
      </p>
      <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 4 }}>{hint}</p>
    </div>
  );
}

function ClaimRow({ claim }) {
  const status = STATUS_META[claim.status];
  return (
    <div style={{ padding: '14px 22px', borderBottom: '1px solid #F0F2F8', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>💰</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontWeight: 600, fontSize: 14, color: '#0F1225', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {claim.redemption?.coupon?.merchant?.name || 'Cashback'}
        </p>
        <p style={{ fontSize: 12, color: '#8B94A8' }}>
          {claim.redemption?.coupon?.title || claim.pool?.campaign_name} · {new Date(claim.created_at).toLocaleDateString()}
        </p>
      </div>
      <div style={{ textAlign: 'right' }}>
        <p style={{ fontWeight: 800, fontSize: 15, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>+${Number(claim.amount).toFixed(2)}</p>
        {status && <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, color: status.color, background: status.bg, marginTop: 2 }}>{status.label}</span>}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #F0F2F8' }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: '#8B94A8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 14, color: '#0F1225', fontWeight: 500 }}>{value}</p>
    </div>
  );
}

function RequestPayoutModal({ available, minimum, onClose, onSuccess }) {
  const safeAvailable = Number(available) || 0;
  const safeMinimum   = Number(minimum)   || 0;
  const [amount, setAmount] = useState(safeAvailable.toFixed(2));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { requestPayout } = usePayouts();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      await requestPayout(parseFloat(amount));
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not request payout.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ overflow: 'hidden' }}>
        <div style={{ background: 'var(--brand-gradient)', color: '#fff', padding: '24px 28px', position: 'relative' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.18)', border: 'none', width: 32, height: 32, borderRadius: '50%', color: '#fff', fontSize: 18, cursor: 'pointer' }}>×</button>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.9, marginBottom: 4 }}>Request Payout</p>
          <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>Withdraw cashback</h2>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 28 }}>
          {error && <div className="alert alert-danger">{error}</div>}

          <p style={{ fontSize: 14, color: '#4A5568', marginBottom: 18, lineHeight: 1.6 }}>
            Available balance: <strong style={{ color: '#0F1225' }}>${safeAvailable.toFixed(2)}</strong>. Minimum payout is <strong>${safeMinimum.toFixed(2)}</strong>.
          </p>

          <div className="form-group">
            <label className="form-label">Amount (USD)</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#8B94A8', fontSize: 16, fontWeight: 600 }}>$</span>
              <input
                className="form-input" type="number" step="0.01" min={safeMinimum} max={safeAvailable} required
                value={amount} onChange={e => setAmount(e.target.value)}
                style={{ paddingLeft: 28, fontSize: 18, fontWeight: 700, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {[25, 50, 100].filter(n => n <= safeAvailable).map(n => (
                <button key={n} type="button" onClick={() => setAmount(n.toFixed(2))}
                  style={{ padding: '6px 12px', borderRadius: 999, border: '1.5px solid #E4E7F0', background: '#fff', fontSize: 12, fontWeight: 600, color: '#4A5568', cursor: 'pointer', fontFamily: 'inherit' }}
                >${n}</button>
              ))}
              <button type="button" onClick={() => setAmount(safeAvailable.toFixed(2))}
                style={{ padding: '6px 12px', borderRadius: 999, border: '1.5px solid transparent', background: 'var(--brand-gradient)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >All available</button>
            </div>
          </div>

          <p style={{ fontSize: 12, color: '#8B94A8', lineHeight: 1.6, marginBottom: 22 }}>
            Funds typically arrive in your bank account within 1–3 business days via Stripe.
          </p>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-secondary btn-lg" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-lg" disabled={submitting} style={{ flex: 2 }}>
              {submitting ? 'Requesting…' : `Withdraw $${parseFloat(amount || 0).toFixed(2)} →`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
